/**
 * useAgenticOptimize — Shared per-section resume optimization runner.
 *
 * Mirrors the same pipeline used by SingleOptimizeFlow's ProcessingStep
 * (parseResumeSections → concurrent optimizeSection calls → assemble
 *  full optimized text), but exposes reactive state so it can be driven
 * from inside the AI Agent canvas.
 *
 * Phases:
 *   idle       → not started
 *   baseline   → computing ATS baseline (sync, ~50ms)
 *   optimizing → per-section LLM rewrites in flight
 *   done       → all sections rewritten
 *   error      → unrecoverable failure
 */

import { useCallback, useRef, useState } from 'react';

import {
  parseResumeSections,
  sortSectionsByCanonicalOrder,
  normalizeSectionTitles,
  type ResumeSection,
} from '../../dashboard/components/AIResumeOptimizer/utils/parseResumeSections';
import {
  optimizeSection,
  type JobContext,
  type ResumeContext,
} from '../../../services/sectionOptimizationService';
import { analyzeATSCompliance, type ATSComplianceReport } from '../../../utils/atsComplianceAnalyzer';
import { normalizeResumeText } from '../../../utils/pdfTextSanitizer';

// ── Types ────────────────────────────────────────────────────────────────────

export type AgenticOptimizePhase =
  | 'idle'
  | 'baseline'
  | 'optimizing'
  | 'done'
  | 'error';

/** Mirrors the canvas `SectionStatus` type. */
export type AgenticSectionStatus = 'pending' | 'loading' | 'done' | 'error' | 'skipped';

export interface AgenticSectionState {
  title:            string;
  originalContent:  string;
  optimizedContent: string;
  status:           AgenticSectionStatus;
  /** Error detail when status='error' — surfaced in canvas UI. */
  errorDetail?:     string;
}

export interface AgenticBaseline {
  formatting: number;
  keywords:   number;
  structure:  number;
  overall:    number;
  atsRating:  'Excellent' | 'Good' | 'Fair' | 'Poor';
  matchScore: number;          // semantic-ish match factor 0-100
  strengths:  string[];
  warnings:   string[];
}

export interface AgenticFinalResult {
  sections:        Array<{ title: string; content: string }>;
  fullText:        string;
  matchScoreBefore: number;
  matchScoreAfter:  number;
  matchedSkills:    string[];
  missingSkills:    string[];
  improvements:     string[];
  /** Sections that failed to optimize (user should be informed). */
  failedSections?: string[];
}

export interface UseAgenticOptimizeOptions {
  // No options needed — resumeText is passed at start() call time
}

export interface AgenticStartArgs {
  job: JobContext & { id?: string };
  /** Resume plain text — passed here so the hook always uses the latest value, not a stale closure. */
  resumeText: string;
  /** Pre-bucketed sections from Gemini canonical JSON — skips regex parseResumeSections. */
  preParsedSections?: Array<{ title: string; content: string }>;
  /** Precomputed match score from the recommendation engine (0-100). When present,
   *  this is used as a floor for the baseline score to prevent the displayed score
   *  from dropping below what the user saw on the job card. */
  precomputedMatchScore?: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildResumeContext(
  sections:    ResumeSection[],
  fullText?:   string,
): ResumeContext {
  const personalSectionTitles = new Set([
    'PERSONAL INFORMATION', 'CONTACT INFORMATION', 'CONTACT', 'PERSONAL', 'NAME', 'HEADER',
  ]);
  let candidateName: string | undefined;
  const personal = sections.find((s) => personalSectionTitles.has((s.title || '').toUpperCase().trim()));
  if (personal?.content) {
    const firstLine = personal.content.split('\n')[0]?.trim().slice(0, 120);
    if (firstLine && !/^[\d\s\-+@.\/():]+$/.test(firstLine)) candidateName = firstLine;
  }
  const summaryParts = sections.map((s) => `${s.title}: ${(s.content || '').slice(0, 80).replace(/\n/g, ' ')}`);
  const fullResumeSummary = (fullText && fullText.length > 0)
    ? fullText.slice(0, 600).replace(/\s+/g, ' ').trim()
    : summaryParts.join(' | ').slice(0, 600);
  return { candidateName: candidateName || undefined, fullResumeSummary: fullResumeSummary || undefined };
}

const KNOWN_SECTION_HEADERS = new Set([
  'PERSONAL INFORMATION','CONTACT INFORMATION','CONTACT','PROFESSIONAL','PROFESSIONAL SUMMARY',
  'SUMMARY','OBJECTIVE','WORK EXPERIENCE','PROFESSIONAL EXPERIENCE','EXPERIENCE','EMPLOYMENT',
  'EDUCATION','SKILLS','TECHNICAL SKILLS','KEY SKILLS','CERTIFICATIONS','CERTIFICATIONS & LICENSES',
  'LANGUAGES','AWARDS','AWARDS & HONORS','HONORS','PROJECTS','ADDITIONAL INFORMATION',
  'REFERENCES','PUBLICATIONS','RESEARCH','TRAINING','TRAINING & DEVELOPMENT','LEADERSHIP',
  'AFFILIATIONS','HOBBIES','HOBBIES & INTERESTS',
]);

function cleanContent(content: string): string {
  const lines = content.split('\n');
  while (lines.length > 0 && lines[lines.length - 1].trim() === '') lines.pop();
  while (lines.length > 0) {
    const last = lines[lines.length - 1].trim();
    const upper = last.toUpperCase();
    if (
      last.length > 0 && last.length < 45 &&
      (KNOWN_SECTION_HEADERS.has(upper) || (last === upper && /^[A-Z][A-Z\s&/\-:]+$/.test(upper)))
    ) { lines.pop(); } else { break; }
  }
  return lines.join('\n').trim();
}

function toTitleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function diffSkills(
  oldText: string,
  newText: string,
  jobSkills: string[],
): { matched: string[]; missing: string[] } {
  const newLower = newText.toLowerCase();
  const oldLower = oldText.toLowerCase();
  const matched: string[] = [];
  const missing: string[] = [];
  for (const raw of jobSkills) {
    const skill = raw.trim();
    if (!skill) continue;
    const lc = skill.toLowerCase();
    if (newLower.includes(lc) || oldLower.includes(lc)) matched.push(skill);
    else missing.push(skill);
  }
  return { matched: matched.slice(0, 12), missing: missing.slice(0, 8) };
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useAgenticOptimize(_opts: UseAgenticOptimizeOptions = {}) {
  const [phase,           setPhase]           = useState<AgenticOptimizePhase>('idle');
  const [baseline,        setBaseline]        = useState<AgenticBaseline | null>(null);
  const [baselineReport,  setBaselineReport]  = useState<ATSComplianceReport | null>(null);
  const [sections,        setSections]        = useState<AgenticSectionState[]>([]);
  const [finalResult,     setFinalResult]     = useState<AgenticFinalResult | null>(null);
  const [error,           setError]           = useState<string | null>(null);

  const abortRef    = useRef<AbortController | null>(null);
  const sectionsRef = useRef<AgenticSectionState[]>([]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    sectionsRef.current = [];
    setPhase('idle');
    setBaseline(null);
    setBaselineReport(null);
    setSections([]);
    setFinalResult(null);
    setError(null);
  }, []);

  const start = useCallback(async ({ job, resumeText, preParsedSections, precomputedMatchScore }: AgenticStartArgs) => {
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;

    console.log('[useAgenticOptimize] 🚀 Starting optimization', {
      jobTitle: job.title,
      company: job.company,
      resumeTextLength: resumeText?.length ?? 0,
      hasPreParsedSections: Boolean(preParsedSections?.length),
      preParsedSectionsCount: preParsedSections?.length ?? 0,
      precomputedMatchScore,
    });

    setError(null);
    setFinalResult(null);

    if (!resumeText || resumeText.trim().length < 50) {
      console.error('[useAgenticOptimize] ❌ Resume too short:', resumeText?.length ?? 0);
      setError('Resume is too short to optimize. Please complete your profile first.');
      setPhase('error');
      return;
    }

    // ── Phase 1: Baseline ATS analysis (sync) ────────────────────────────
    console.log('[useAgenticOptimize] 📊 Phase 1: Computing baseline ATS analysis...');
    setPhase('baseline');
    let baselineOverall = 50;
    try {
      const ats = analyzeATSCompliance(resumeText, {
        skills:       job.skills ?? [],
        description:  job.description ?? '',
        requirements: job.requirements ?? [],
      });

      const skills = job.skills ?? [];
      const lower  = resumeText.toLowerCase();
      const hits   = skills.filter((s) => lower.includes(s.toLowerCase())).length;
      const match  = skills.length > 0 ? Math.round((hits / skills.length) * 100) : 50;
      const factor = Math.max(0.5, Math.min(1.0, match / 100));
      const localOverall = Math.round(ats.overallScore * factor);
      // Use the precomputed score from recommendation engine as a floor — the user saw
      // that score on the job card, so we shouldn't show anything lower here.
      baselineOverall = precomputedMatchScore !== undefined
        ? Math.max(localOverall, Math.round(precomputedMatchScore))
        : localOverall;

      // Adjusted report for the canvas (so the visible scores reflect job match)
      const adjustedReport: ATSComplianceReport = {
        ...ats,
        overallScore: baselineOverall,
        sections: {
          formatting: Math.round(ats.sections.formatting * factor),
          keywords:   Math.round(ats.sections.keywords   * factor),
          structure:  Math.round(ats.sections.structure  * factor),
          content:    Math.round(ats.sections.content    * factor),
        },
      };
      setBaselineReport(adjustedReport);

      setBaseline({
        formatting: adjustedReport.sections.formatting,
        keywords:   adjustedReport.sections.keywords,
        structure:  adjustedReport.sections.structure,
        overall: baselineOverall,
        atsRating:  baselineOverall >= 85 ? 'Excellent' : baselineOverall >= 70 ? 'Good' : baselineOverall >= 50 ? 'Fair' : 'Poor',
        matchScore: match,
        strengths:  ats.strengths.slice(0, 3),
        warnings:   ats.warnings.slice(0, 3),
      });
    } catch (err) {
      console.warn('[useAgenticOptimize] Baseline analysis failed:', err);
      setBaseline({
        formatting: 50, keywords: 40, structure: 60, overall: 50, atsRating: 'Fair',
        matchScore: 40, strengths: [], warnings: [],
      });
    }

    // Small delay so user actually sees the baseline before sections start
    await new Promise((r) => setTimeout(r, 600));
    if (ctl.signal.aborted) return;

    // ── Phase 2: Parse & init sections ────────────────────────────────────
    console.log('[useAgenticOptimize] 📝 Phase 2: Parsing sections...');
    const parsed: ResumeSection[] = (preParsedSections && preParsedSections.length > 0)
      ? preParsedSections.map((s) => ({
          title: s.title,
          content: s.content,
          preview: s.content.slice(0, 120),
        }))
      : parseResumeSections(normalizeResumeText(resumeText));
    const original = sortSectionsByCanonicalOrder(normalizeSectionTitles(parsed));

    console.log('[useAgenticOptimize] 📋 Sections parsed:', original.map((s) => ({
      title: s.title,
      contentLength: s.content?.length ?? 0,
      preview: s.content?.slice(0, 80) ?? '',
    })));

    // Log Personal Information section specifically for debugging header issues
    const personalSection = original.find((s) => 
      /personal|contact|header/i.test(s.title)
    );
    if (personalSection) {
      console.log('[useAgenticOptimize] 👤 Personal Info section:', {
        title: personalSection.title,
        content: personalSection.content?.slice(0, 200),
        hasWorkBullets: /•.*\b(engineered|developed|led|managed|implemented)\b/i.test(personalSection.content || ''),
      });
    }

    if (original.length === 0) {
      console.error('[useAgenticOptimize] ❌ No sections detected in resume');
      setError('Could not detect any sections in your resume.');
      setPhase('error');
      return;
    }

    const initialSections: AgenticSectionState[] = original.map((s) => ({
      title:            s.title,
      originalContent:  s.content || '',
      optimizedContent: '',
      status:           'loading',
    }));
    sectionsRef.current = initialSections;
    setSections(initialSections);
    console.log('[useAgenticOptimize] ⚡ Phase 2: Starting section optimization...', {
      sectionsCount: initialSections.length,
      sectionTitles: initialSections.map((s) => s.title),
    });
    setPhase('optimizing');

    const resumeContext = buildResumeContext(original, resumeText);

    // Fire all section calls concurrently
    console.log('[useAgenticOptimize] 🔄 Firing concurrent optimization for all sections...');
    await Promise.allSettled(original.map((sec, idx) =>
      optimizeSection(
        { title: sec.title, content: sec.content || '' },
        job,
        ctl.signal,
        resumeContext,
      )
        .then((result) => {
          if (ctl.signal.aborted) return;
          const hasError = result.skipped && (result as { error?: string }).error;
          const status: AgenticSectionStatus = hasError ? 'error' : result.skipped ? 'skipped' : 'done';
          
          console.log(`[useAgenticOptimize] ✅ Section "${sec.title}" completed`, {
            status,
            originalLength: sec.content?.length ?? 0,
            optimizedLength: result.optimizedContent?.length ?? 0,
            skipped: result.skipped,
            hasError,
          });
          
          // Log Personal Information section content for debugging
          if (/personal|contact|header/i.test(sec.title)) {
            console.log(`[useAgenticOptimize] 👤 Personal Info section optimized:`, {
              originalContent: sec.content?.slice(0, 150),
              optimizedContent: result.optimizedContent?.slice(0, 150),
              hasWorkBullets: /•.*\b(engineered|developed|led|managed|implemented)\b/i.test(result.optimizedContent || ''),
            });
          }
          
          sectionsRef.current = sectionsRef.current.map((s, i) =>
            i === idx
              ? {
                  ...s,
                  optimizedContent: cleanContent(result.optimizedContent),
                  status,
                  errorDetail: hasError ? (result as { error?: string }).error : undefined,
                }
              : s
          );
          setSections([...sectionsRef.current]);
        })
        .catch((err) => {
          if ((err as Error)?.name === 'AbortError') return;
          const errMsg = (err as Error)?.message ?? 'Section optimization failed';
          console.error(`[useAgenticOptimize] ❌ Section "${sec.title}" failed:`, errMsg);
          sectionsRef.current = sectionsRef.current.map((s, i) =>
            i === idx
              ? {
                  ...s,
                  optimizedContent: s.originalContent,
                  status: 'error' as AgenticSectionStatus,
                  errorDetail: errMsg,
                }
              : s
          );
          setSections([...sectionsRef.current]);
        })
    ));

    if (ctl.signal.aborted) return;

    // ── Phase 3: Assemble final result ────────────────────────────────────
    console.log('[useAgenticOptimize] 🏁 Phase 3: Assembling final result...');
    const finalSections = sectionsRef.current.map((s) => ({
      title:   s.title,
      content: s.optimizedContent || s.originalContent,
    }));
    
    // Log final sections for debugging
    console.log('[useAgenticOptimize] 📋 Final sections:', finalSections.map((s) => ({
      title: s.title,
      contentLength: s.content?.length ?? 0,
      preview: s.content?.slice(0, 80) ?? '',
    })));
    
    // Check for Personal Information section issues
    const finalPersonal = finalSections.find((s) => /personal|contact|header/i.test(s.title));
    if (finalPersonal) {
      const hasWorkBullets = /•.*\b(engineered|developed|led|managed|implemented)\b/i.test(finalPersonal.content || '');
      console.log('[useAgenticOptimize] 👤 Final Personal Info:', {
        title: finalPersonal.title,
        content: finalPersonal.content?.slice(0, 200),
        hasWorkBullets,
        issue: hasWorkBullets ? '⚠️ WORK EXPERIENCE BULLETS DETECTED IN PERSONAL INFO!' : '✅ Clean',
      });
    }
    
    // Format with markdown section headers for proper rendering
    const fullText = finalSections
      .map((s) => `## ${toTitleCase(s.title)}\n\n${s.content}`)
      .join('\n\n');

    const matchScoreBefore = baselineOverall;

    // Re-analyze the optimized resume to get actual post-optimization ATS score
    let matchScoreAfter = Math.min(100, Math.round(matchScoreBefore * 1.25 + 15));
    try {
      const postAts = analyzeATSCompliance(fullText, {
        skills:       job.skills ?? [],
        description:  job.description ?? '',
        requirements: job.requirements ?? [],
      });
      const postSkills = job.skills ?? [];
      const postLower  = fullText.toLowerCase();
      const postHits   = postSkills.filter((s) => postLower.includes(s.toLowerCase())).length;
      const postMatch  = postSkills.length > 0 ? Math.round((postHits / postSkills.length) * 100) : 70;
      const postFactor = Math.max(0.6, Math.min(1.0, postMatch / 100));
      const computedAfter = Math.round(postAts.overallScore * postFactor);
      // Use the higher of the two calculations (heuristic boost vs actual re-analysis)
      matchScoreAfter = Math.max(matchScoreAfter, computedAfter);
      // Ensure post-optimization is always higher than pre-optimization
      matchScoreAfter = Math.max(matchScoreAfter, matchScoreBefore + 10);
      matchScoreAfter = Math.min(100, matchScoreAfter);
    } catch {
      // Fallback to formula-based score
    }

    const { matched, missing } = diffSkills(resumeText, fullText, job.skills ?? []);

    const failedSections = sectionsRef.current
      .filter((s) => s.status === 'error')
      .map((s) => s.title);

    const improvements = [
      'Rewrote summary and experience for the target role',
      'Wove role-specific keywords into Skills and Experience',
      'Tightened bullets with stronger action verbs',
    ];
    if (failedSections.length > 0) {
      improvements.push(`Note: ${failedSections.join(', ')} could not be optimized — original content was kept`);
    }

    const result = {
      sections:         finalSections,
      fullText,
      matchScoreBefore,
      matchScoreAfter,
      matchedSkills:    matched,
      missingSkills:    missing,
      improvements,
      failedSections:   failedSections.length > 0 ? failedSections : undefined,
    };
    
    console.log('[useAgenticOptimize] 🎉 Optimization complete!', {
      sectionsCount: finalSections.length,
      matchScoreBefore,
      matchScoreAfter,
      scoreImprovement: matchScoreAfter - matchScoreBefore,
      matchedSkillsCount: matched.length,
      missingSkillsCount: missing.length,
      failedSections: failedSections.length > 0 ? failedSections : 'none',
    });
    
    setFinalResult(result);
    setPhase('done');
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return {
    phase,
    baseline,
    baselineReport,
    sections,
    finalResult,
    error,
    start,
    abort,
    reset,
  };
}
